import { Table } from "antd";
import {
  getCreditLogsByCustomerPackageId,
  getExpiryDateLogsByCustomerPackageId,
  getPaymentMethodLogsByCustomerPackageId,
  getStatusLogsByCustomerPackageId,
} from "hooks/queries/customerpackage";
import { translate } from "shared/languages";
import { th } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import {
  INACTIVE_REASON_LABELS,
  PACKAGE_VALIDITY_LABELS,
  UPGRADE_CANCELLED_VOID_REASON,
  UPGRADED_STATUS_REASON,
} from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate } from "shared/utils/dayjs";
import type { TableProps } from "antd";
import type { CustomerPackageMember } from "shared/types";
import { InactiveReason } from "shared/types/entity";
import { CreditLogReason, ExpiryLogReason } from "shared/types/enum";

type HistoryRow = {
  id: string;
  created_at: string;
  actor_full_name: string;
  field: "credit" | "payment_method" | "expiry_date" | "status" | "sharing";
  before: string;
  after: string;
  // Expiry rows the validity-start recompute wrote carry why (activation,
  // first class changed, reverted to the deadline); manual edits have none.
  reason?: ExpiryLogReason | null;
  // Credit rows the system wrote: an import's carried-over balance, or a
  // credit returned for a class booked on the package this one was upgraded
  // from. Manual edits have none.
  creditReason?: CreditLogReason | null;
  // Status rows: why the package was switched off or back on. A VoidReason
  // for a void, UPGRADED_STATUS_REASON when an upgrade retired it,
  // UPGRADE_CANCELLED_VOID_REASON on both halves when the upgrade was
  // cancelled. Its own field rather than reusing `reason`, which is the
  // expiry ladder's enum. A void that was undone and re-done keeps each
  // reason on its own row. A void's free-text note is not shown here (it can
  // name the wrong client); it lives on the package card.
  statusReason?: string | null;
  // The upgrade rows carry the OTHER tier's name as their note, shown beside
  // the label ("Upgraded · 10x One On One Senior").
  statusNote?: string | null;
  // A status row with no upgrade reason is one of two events, named as such
  // in the Jenis column: a void (package switched off) or the undo of one
  // (switched back on).
  statusUndo?: boolean;
};

const formatExpiryDateOrDash = (value: string | null) =>
  value ? formatDate(value) : "-";

// Same fixed words as the package card's validity badge (CustomerPackageCard).
const formatIsValid = (isValid: boolean) =>
  PACKAGE_VALIDITY_LABELS[isValid ? "active" : "inactive"];

// Unified "what's been manually changed on this package" timeline — merges
// the credit-edit log, the payment-method-edit log, the expiry-date-edit
// log, and the status (invalidate/"delete") log (four separate backend
// tables/endpoints, genuinely different facts) into one table, since all
// four are low-frequency audit logs on the same record and checking one
// place is more useful than checking several near-empty tabs.
const PackageHistoryTable = ({
  customerPackageId,
  members = [],
}: {
  customerPackageId?: string;
  // Sharing has no log table of its own: the member rows carry their own
  // granted/revoked timestamps and actors, so the history is derived from them
  // and rides the package-detail fetch the page already made.
  members?: CustomerPackageMember[];
}) => {
  const { data: creditLogs = [], isLoading: isCreditLoading } =
    getCreditLogsByCustomerPackageId(customerPackageId);
  const { data: paymentMethodLogs = [], isLoading: isPaymentMethodLoading } =
    getPaymentMethodLogsByCustomerPackageId(customerPackageId);
  const { data: expiryDateLogs = [], isLoading: isExpiryDateLoading } =
    getExpiryDateLogsByCustomerPackageId(customerPackageId);
  const { data: statusLogs = [], isLoading: isStatusLoading } =
    getStatusLogsByCustomerPackageId(customerPackageId);

  const rows: HistoryRow[] = [
    ...creditLogs.map((log) => ({
      id: log.id,
      created_at: log.created_at,
      actor_full_name: log.actor?.full_name ?? translate("label.actor_system"),
      field: "credit" as const,
      before: String(log.old_remaining_credits),
      after: String(log.new_remaining_credits),
      creditReason: log.reason ?? null,
    })),
    ...paymentMethodLogs.map((log) => ({
      id: log.id,
      created_at: log.created_at,
      actor_full_name: log.actor.full_name,
      field: "payment_method" as const,
      before: log.old_payment_method_name ?? "-",
      after: log.new_payment_method_name ?? "-",
    })),
    ...expiryDateLogs.map((log) => ({
      id: log.id,
      created_at: log.created_at,
      // System-written activation rows have no actor (the activity-log
      // "Sistem" convention).
      actor_full_name: log.actor?.full_name ?? translate("label.actor_system"),
      field: "expiry_date" as const,
      // A package whose clock has not started shows no expiry, so the side
      // of the transition that was "belum aktif" reads "-" (the stored date
      // there was only the auto-start ceiling).
      before:
        log.reason === ExpiryLogReason.Activated ||
        log.reason === ExpiryLogReason.ManualActivation
          ? "-"
          : formatExpiryDateOrDash(log.old_expiry_date),
      after:
        log.reason === ExpiryLogReason.Deactivated
          ? "-"
          : formatExpiryDateOrDash(log.new_expiry_date),
      reason: log.reason ?? null,
    })),
    ...statusLogs.map((log) => ({
      id: log.id,
      created_at: log.created_at,
      actor_full_name: log.actor.full_name,
      field: "status" as const,
      before: formatIsValid(log.old_is_valid),
      after: formatIsValid(log.new_is_valid),
      statusReason: log.reason ?? null,
      statusNote: log.note ?? null,
      statusUndo: log.new_is_valid,
    })),
    // One row per grant, plus one per revoke, so re-granting after a revoke
    // reads as two events rather than overwriting the first. Before/after
    // carry just the client's name: a grant is "- -> name", a revoke is
    // "name -> -", the Field column already says it is sharing (Andrew,
    // 2026-09-21).
    ...members.map((m) => ({
      id: `share-add-${m.id}`,
      created_at: m.created_at,
      actor_full_name: m.created_by_name ?? "-",
      field: "sharing" as const,
      before: "-",
      after: m.full_name ?? "-",
    })),
    ...members
      .filter((m) => m.deleted_at)
      .map((m) => ({
        id: `share-remove-${m.id}`,
        created_at: m.deleted_at as string,
        actor_full_name: m.deleted_by_name ?? "-",
        field: "sharing" as const,
        before: m.full_name ?? "-",
        after: "-",
      })),
  ];

  // Only 5 plain columns and no content that needs extra room — split the
  // table evenly instead of letting AntD auto-size each to its content.
  const columns: TableProps<HistoryRow>["columns"] = [
    {
      title: th(translate("label.time")),
      dataIndex: "created_at",
      key: "created_at",
      width: "20%",
      render: (created_at: string) => (
        <span>{formatDate(created_at, DEFAULT_DATE_FORMAT + ", HH:mm")}</span>
      ),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: th(translate("label.edited_by")),
      dataIndex: "actor_full_name",
      key: "actor_full_name",
      width: "20%",
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
    },
    {
      title: th(translate("label.field_changed")),
      dataIndex: "field",
      key: "field",
      width: "20%",
      render: (field: HistoryRow["field"], row: HistoryRow) => {
        if (field === "credit")
          return row.creditReason
            ? `${translate("label.credits")} · ${translate(`credit_reason.${row.creditReason}`)}`
            : translate("label.credits");
        if (field === "payment_method") return translate("paymentMethods.col_payment_method");
        if (field === "expiry_date")
          return row.reason
            ? `${translate("label.expiry_date")} · ${translate(`expiry_reason.${row.reason}`)}`
            : translate("label.expiry_date");
        if (field === "sharing") return translate("package_sharing.title");
        // The upgrade rows come first, BEFORE the undo test: the old
        // package's restore row is a true transition too, and would otherwise
        // read "Undo Void". "Upgraded · <new tier>" when an upgrade retired
        // it (the status word, never translated), "Upgrade Dibatalkan" on
        // both halves when the upgrade was cancelled.
        if (row.statusReason === UPGRADED_STATUS_REASON)
          return row.statusNote
            ? `${INACTIVE_REASON_LABELS[InactiveReason.UPGRADED]} · ${row.statusNote}`
            : INACTIVE_REASON_LABELS[InactiveReason.UPGRADED];
        if (row.statusReason === UPGRADE_CANCELLED_VOID_REASON)
          return row.statusNote
            ? `${translate("label.history_cancel_upgrade")} · ${row.statusNote}`
            : translate("label.history_cancel_upgrade");
        // "Void · Salah Produk" / "Undo Void", never a bare "Status": the
        // column says what happened, the two beside it say what it changed.
        if (row.statusUndo) return translate("label.history_undo_void");
        return row.statusReason
          ? `${translate("label.history_void")} · ${translate(`void_reason.${row.statusReason}`)}`
          : translate("label.history_void");
      },
    },
    {
      title: th(translate("label.before")),
      dataIndex: "before",
      key: "before",
      width: "20%",
    },
    {
      title: th(translate("label.after")),
      dataIndex: "after",
      key: "after",
      width: "20%",
    },
  ];

  return (
    <Table
      showSorterTooltip={false}
      rowKey="id"
      columns={columns}
      dataSource={rows}
      loading={isCreditLoading || isPaymentMethodLoading || isExpiryDateLoading || isStatusLoading}
      pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
    />
  );
};

export default PackageHistoryTable;
